import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button.jsx'; // Pagination buttons
import { Skeleton } from "../../components/ui/skeleton.jsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.jsx";
import { useToast } from "../../hooks/use-toast.js";
import CallRecording from '../../components/CallRecording/CallRecording.jsx';
import { DateTime } from 'luxon';
import axios from '../../lib/axios.js';
import { Badge } from '@/components/ui/badge';
import { ChevronLeftIcon, ChevronRightIcon, PhoneIncomingIcon, PhoneOutgoingIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.jsx';
import CallScoreBadge from '@/components/CallScoreBadge/index.jsx';
import CallOutcomeBadge from '@/components/CallOutcomeBadge/index.jsx';

const Calls = ({ communityId, timezone }) => {
    const [callData, setCallData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalCalls, setTotalCalls] = useState(0);
    const [limit] = useState(10); // Number of items per page
    const [tab, setTab] = useState('outcomes');

    const navigate = useNavigate();
    const { toast } = useToast();

    useEffect(() => {
        const fetchCallData = async () => {
            setIsLoading(true);
            try {
                const outcomeParam = tab === 'outcomes' ? `&has_outcome=true` : '';
                const response = await axios.get(`/communities/${communityId}/calls?page=${page}&limit=${limit}${outcomeParam}`);
                const { data } = response;
                setCallData(data.calls);
                setTotalCalls(data.total);
            } catch (error) {
                console.error('Error fetching call data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load call data. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchCallData();
    }, [communityId, page, limit, tab, toast]);

    const totalPages = Math.ceil(totalCalls / limit);

    const handlePreviousPage = () => {
        if (page > 1) setPage(page - 1);
    };

    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };

    useEffect(() => {
        setPage(1);
    }, [tab]);

    const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

    const formatPhoneNumber = (phone) => {
        if (!phone) {
            return '';
        }

        const digits = phone.replace(/\D/g, '').slice(-10); // Keep last 10 digits

        if (digits.length !== 10) {
            return phone;
        }
        const [, area, prefix, line] = digits.match(/(\d{3})(\d{3})(\d{4})/) || [];

        return `(${area}) ${prefix}-${line}`;
    };

    const formatDate = (date) => {
        const dt = DateTime.fromISO(date).setZone(timezone);
        const currentYear = DateTime.now().setZone(timezone).year;

        // Choose format string based on whether the date is in the current year
        const fmt = dt.year === currentYear
            ? 'MMM d, hh:mm a'        // e.g., “May 05, 09:00 AM”
            : 'MMM d, yyyy hh:mm a';  // e.g., “May 05, 2024 09:00 AM”

        return dt.toFormat(fmt);
    };

    return (
        <div className="space-y-4">

            <h1 className='text-gray-500 text-lg font-medium'>
                Calls
            </h1>
            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList>
                    <TabsTrigger value="outcomes">Outcomes</TabsTrigger>
                    <TabsTrigger value="all">All Calls</TabsTrigger>
                </TabsList>
            </Tabs>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
                <Table className="rounded-lg">
                    <TableHeader className="bg-gray-100 rounded-lg">

                        <TableRow className="rounded-lg">
                            <TableHead>Date</TableHead>
                            <TableHead>Prospect</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Agent Quality</TableHead>
                            <TableHead>Outcome</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!!isLoading &&
                            Array.from({ length: 10 }, (_, i) => i).map((i) =>
                                <TableRow key={i}>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                                </TableRow>
                            )
                        }
                        {!isLoading && callData?.map((call) => (
                            <TableRow key={call.id} onClick={() => navigate(`/calls/${call.id}`)}>
                                <TableCell>{formatDate(call.created_at)}</TableCell>
                                <TableCell>
                                    {call.prospect.first_name?.trim()
                                        ? `${capitalize(call.prospect.first_name)} ${capitalize(call.prospect.last_name || '')}`
                                        : formatPhoneNumber(call.prospect.phone_number)}
                                </TableCell>
                                <TableCell>
                                    {call.direction === 'incoming' && <PhoneIncomingIcon className='w-4 h-4 text-blue-500' />}
                                    {call.direction === 'outgoing' && <PhoneOutgoingIcon className='w-4 h-4 text-green-500' />}
                                </TableCell>
                                <TableCell>
                                    <CallScoreBadge score={call?.call_score?.score} />
                                </TableCell>
                                <TableCell>
                                    <CallOutcomeBadge outcome={call?.call_score?.primary_outcome} />
                                </TableCell>
                                <TableCell>
                                    <CallRecording id={call.id} fileName={call.file_name} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination controls */}
            <div className="gap-6 flex justify-end mt-4">
                <Button size="sm" variant="outline" onClick={handlePreviousPage} disabled={page === 1}><ChevronLeftIcon className='w-4 h-4' /></Button>
                <span className='text-grey-500 self-center'>Page {page} of {totalPages}</span>
                <Button size="sm" variant="outline" onClick={handleNextPage} disabled={page === totalPages}><ChevronRightIcon className='w-4 h-4' /> </Button>
            </div>
        </div>
    );
};

export default Calls;